"use client";

import type { ForumComposePost } from "../forum.types";
import { usePostComposer } from "./use-post-composer";
import { Button, Input, Alert, SegmentedControl } from "@softmax/design-system";
import * as stylex from "@stylexjs/stylex";
import { styles } from "../forum.styles";
import { composerStyles } from "../forum-composer.styles";

export function PostComposer({
  compose,
}: Readonly<{ compose: ForumComposePost }>) {
  const {
    open,
    setOpen,
    title,
    setTitle,
    body,
    setBody,
    format,
    setFormat,
    image,
    imageInput,
    trigger,
    fieldId,
    busy,
    submit,
    changeImage,
    removeImage,
    discard,
  } = usePostComposer(compose);
  return (
    <div>
      <Button ref={trigger} aria-expanded={open} onClick={() => setOpen(!open)}>
        Write a post
      </Button>
      {open && (
        <form {...stylex.props(composerStyles.form)} onSubmit={submit}>
          <label
            {...stylex.props(composerStyles.field)}
            htmlFor={`${fieldId}-title`}
          >
            Title
            <Input
              id={`${fieldId}-title`}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              disabled={busy}
            />
          </label>
          {compose.allowPlainText && (
            <SegmentedControl<"markdown" | "text">
              aria-label="Post format"
              value={format}
              onValueChange={setFormat}
              disabled={busy}
              items={[
                { value: "markdown", label: "Markdown" },
                { value: "text", label: "Plain text" },
              ]}
            />
          )}
          <label
            {...stylex.props(composerStyles.field)}
            htmlFor={`${fieldId}-body`}
          >
            Post
            <textarea
              id={`${fieldId}-body`}
              rows={5}
              required
              disabled={busy}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
              {...stylex.props(composerStyles.textarea)}
            />
          </label>
          {compose.imageAccept && (
            <label
              {...stylex.props(composerStyles.field)}
              htmlFor={`${fieldId}-image`}
            >
              Attach image
              <input
                ref={imageInput}
                id={`${fieldId}-image`}
                type="file"
                accept={compose.imageAccept}
                disabled={busy}
                onChange={changeImage}
              />
            </label>
          )}
          {image && (
            <div {...stylex.props(composerStyles.form)}>
              <img
                src={image.previewUrl}
                alt={`Attachment preview: ${image.file.name}`}
                {...stylex.props(styles.attachment)}
              />
              <Button disabled={busy} onClick={removeImage}>
                Remove image
              </Button>
            </div>
          )}
          {compose.error && (
            <Alert tone="danger" announcement="assertive">
              {compose.error}
            </Alert>
          )}
          <div {...stylex.props(composerStyles.actions)}>
            <Button
              type="submit"
              variant="primary"
              disabled={busy || !title.trim() || !body.trim()}
            >
              {busy ? "Posting…" : "Post"}
            </Button>
            <Button disabled={busy} onClick={discard}>
              Discard
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
